import React, { useCallback, useEffect, useState } from "react";
import { FiChevronRight } from "react-icons/fi";
import { get as commsGet } from "../../../service/comms/commsApi";
import { getRoutingRules } from "../../../service/comms/supportService";
import { AvatarRing } from "../../../common/comms/AvatarRing";
import AutoAssignDialog from "./AutoAssignDialog";
import { useAgentsWorkload } from "./useSupportVocabulary";
import { toList } from "./supportUtils";
import { DEPARTMENTS, EXTRA_VIEWS, VIEWS } from "./supportEnums";

/**
 * Department swatch colours, borrowed from the coach ring palette so the whole
 * product uses one set of category colours. Full class strings (Tailwind-safe).
 * @type {Object<string, string>}
 */
const DEPARTMENT_DOTS = {
  Billing: "bg-coach-2",
  Account: "bg-coach-1",
  App: "bg-coach-7",
  Other: "bg-coach-6",
};

/** Section heading inside the rail. */
const RailLabel = ({ children }) => (
  <p className="m-0 px-3 pb-1.5 pt-4 text-[9px] font-bold uppercase tracking-[0.1em] text-gray-400">
    {children}
  </p>
);

/**
 * One saved-view row. The active row carries a 2px left border rather than a
 * filled background, so the count column stays readable and the red zone can
 * be red without shouting over the rest of the rail.
 */
const ViewRow = ({ label, count, active, danger, onClick }) => {
  const state = active
    ? danger
      ? "border-danger bg-danger-bg text-danger-dark font-bold"
      : "border-orange-primary bg-white text-gray-900 font-bold"
    : "border-transparent text-gray-700 hover:bg-white";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "true" : undefined}
      className={`flex w-full items-center gap-2 border-l-2 px-3 py-1.5 text-left text-xs transition-colors ${state}`}
    >
      <span className="flex-1 truncate">{label}</span>
      {count !== undefined && (
        <span className={`font-mono ${active ? "" : "text-gray-400"}`}>{count}</span>
      )}
    </button>
  );
};

/**
 * Left rail: saved views, departments, who's on shift, and the auto-assign
 * switch.
 *
 * Counts come from `GET /support/views/counts` (passed in — the workspace
 * already holds them for the ops bar) and use the same predicates as the views
 * they label, so a number here always matches what clicking it shows.
 *
 * @param {Object} props
 * @param {string} props.view - Active saved view.
 * @param {(view: string) => void} props.onSelectView
 * @param {string} props.department - Active department filter ("" for all).
 * @param {(department: string) => void} props.onSelectDepartment
 * @param {{ views?: Object, departments?: Object }} props.counts
 * @param {string|null} [props.companyId] - For the auto-assign rules.
 * @param {(message: string, type?: string) => void} [props.onFeedback]
 * @param {'full'|'compact'} [props.variant='full'] - `compact` collapses the
 *   whole rail into a dropdown row for narrow viewports.
 */
export function SupportViewsRail({
  view,
  onSelectView,
  department,
  onSelectDepartment,
  counts,
  companyId,
  onFeedback,
  variant = "full",
}) {
  // Shared with the escalate picker and the auto-assign dialog — one request
  // between them, not one each.
  const { agents } = useAgentsWorkload(variant !== "compact");
  // Department labels with round-robin on. `null` = not readable (an agent
  // rather than a manager), which hides the control entirely.
  const [enabledPools, setEnabledPools] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const viewCounts = counts?.views || {};
  const departmentCounts = counts?.departments || {};

  // Loaded in both variants — the compact rail is what a laptop or tablet
  // gets, and hiding the desk's routing switch at those widths would make it
  // reachable only from a wide monitor.
  const loadRoutingRules = useCallback(() => {
    getRoutingRules(commsGet)
      .then((payload) => {
        const rules = toList(payload, ["rules", "items", "data"]);
        setEnabledPools(
          rules
            .filter((r) => r.is_enabled === true)
            .map((r) => r.department || "All")
        );
      })
      // Manager tier — an agent simply doesn't get the control.
      .catch(() => setEnabledPools(null));
  }, []);

  useEffect(() => {
    loadRoutingRules();
  }, [loadRoutingRules]);

  if (variant === "compact") {
    return (
      <div className="flex flex-shrink-0 items-center gap-2 border-b border-gray-200 bg-rail-bg px-2.5 py-2">
        <select
          value={view}
          onChange={(e) => onSelectView(e.target.value)}
          aria-label="Ticket view"
          className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-primary/40"
        >
          {[...VIEWS, ...EXTRA_VIEWS].map((v) => (
            <option key={v.key} value={v.key}>
              {viewCounts[v.key] === undefined
                ? v.label
                : `${v.label} · ${viewCounts[v.key]}`}
            </option>
          ))}
        </select>
        <select
          value={department}
          onChange={(e) => onSelectDepartment(e.target.value)}
          aria-label="Department"
          className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-primary/40"
        >
          <option value="">All depts</option>
          {DEPARTMENTS.map((d) => (
            <option key={d} value={d}>
              {departmentCounts[d] === undefined ? d : `${d} · ${departmentCounts[d]}`}
            </option>
          ))}
        </select>

        {enabledPools !== null && (
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            title={
              enabledPools.length === 0
                ? "Auto-assign is off"
                : `Auto-assign: ${enabledPools.join(", ")}`
            }
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
          >
            <span
              aria-hidden="true"
              className={`h-1.5 w-1.5 rounded-full ${
                enabledPools.length > 0 ? "bg-success" : "bg-gray-300"
              }`}
            />
            Auto-assign
          </button>
        )}

        {dialogOpen && (
          <AutoAssignDialog
            companyId={companyId}
            onClose={() => {
              setDialogOpen(false);
              loadRoutingRules();
            }}
            onFeedback={onFeedback}
          />
        )}
      </div>
    );
  }

  return (
    <nav
      aria-label="Ticket views"
      className="flex w-[200px] flex-shrink-0 flex-col border-r border-gray-200 bg-rail-bg"
    >
      <div className="min-h-0 flex-1 overflow-y-auto pb-3">
        <RailLabel>Views</RailLabel>
        {[...VIEWS, ...EXTRA_VIEWS].map((v) => (
          <ViewRow
            key={v.key}
            label={v.label}
            // Only the server's own eight are counted; the extras have none.
            count={viewCounts[v.key]}
            active={view === v.key}
            danger={v.key === "red_zone"}
            onClick={() => onSelectView(v.key)}
          />
        ))}

        <RailLabel>Department</RailLabel>
        <ViewRow
          label="All departments"
          active={!department}
          onClick={() => onSelectDepartment("")}
        />
        {DEPARTMENTS.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => onSelectDepartment(department === d ? "" : d)}
            aria-pressed={department === d}
            className={`flex w-full items-center gap-2 border-l-2 px-3 py-1.5 text-left text-xs transition-colors ${
              department === d
                ? "border-orange-primary bg-white font-bold text-gray-900"
                : "border-transparent text-gray-700 hover:bg-white"
            }`}
          >
            <span
              aria-hidden="true"
              className={`h-1.5 w-1.5 rounded-sm ${DEPARTMENT_DOTS[d] || "bg-coach-6"}`}
            />
            <span className="flex-1 truncate">{d}</span>
            {departmentCounts[d] !== undefined && (
              <span className="font-mono text-gray-400">{departmentCounts[d]}</span>
            )}
          </button>
        ))}

        {agents.length > 0 && (
          <>
            <RailLabel>Team on shift</RailLabel>
            <ul className="flex flex-col gap-1.5 px-3">
              {agents.map((agent, i) => (
                <li
                  key={agent.userId}
                  className={`flex items-center gap-2 ${agent.offShift ? "opacity-50" : ""}`}
                  title={
                    agent.departments?.length
                      ? `${agent.availability} · ${agent.departments.join(", ")}`
                      : agent.availability
                  }
                >
                  <AvatarRing name={agent.name} colorIndex={i % 8} size="xs" />
                  <span
                    className={`flex-1 truncate text-[11px] ${
                      agent.overloaded ? "font-semibold text-danger-dark" : "text-gray-700"
                    }`}
                  >
                    {agent.name}
                    {agent.offShift && " · off shift"}
                  </span>
                  <span
                    className={`font-mono text-[10px] ${
                      agent.overloaded ? "font-bold text-danger-dark" : "text-gray-500"
                    }`}
                  >
                    {agent.openCount}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* Opens the full picture rather than toggling blind: which queues route
          themselves, and who each one would actually reach. */}
      {enabledPools !== null && (
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          className="flex flex-shrink-0 items-center gap-2 border-t border-gray-200 px-3 py-2.5 text-left transition-colors hover:bg-white"
        >
          <div className="min-w-0 flex-1">
            <p className="m-0 text-[10px] font-bold uppercase tracking-wide text-gray-500">
              Auto-assign
            </p>
            <p className="m-0 mt-0.5 truncate text-[10px] text-gray-400">
              {enabledPools.length === 0
                ? "Off · tickets wait in the pool"
                : `Round-robin · ${enabledPools.join(", ")}`}
            </p>
          </div>
          <span
            aria-hidden="true"
            className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${
              enabledPools.length > 0 ? "bg-success" : "bg-gray-300"
            }`}
          />
          <FiChevronRight aria-hidden="true" size={13} className="shrink-0 text-gray-400" />
        </button>
      )}

      {dialogOpen && (
        <AutoAssignDialog
          companyId={companyId}
          onClose={() => {
            setDialogOpen(false);
            loadRoutingRules();
          }}
          onFeedback={onFeedback}
        />
      )}
    </nav>
  );
}

export default SupportViewsRail;
